// components/project-details/InvoicesTab.js
import React from 'react';
import { Link } from 'react-router-dom';
import styles from './ProjectDetails.module.css';

const InvoicesTab = ({ project, invoices }) => {
  // Format currency helper
  const formatCurrency = (amountInPence) => {
    if (!amountInPence) return '£0.00';
    return `£${(amountInPence / 100).toFixed(2)}`;
  };

  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Calculate totals
  const totalInvoiced = project.financials?.totalInvoicedAmount || 0;
  const totalCommission = project.financials?.totalCommissionCollected || 0;
  const yourPayout = totalInvoiced - totalCommission;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-l)' }}>
      {/* Header with Create Invoice Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: 'var(--spacing-s)'
      }}>
        <h2 className={styles.sectionTitle}>Invoices & Payments</h2>
        <Link 
          to={`/invoices/new?project=${project.id}`}
          className={styles.actionButton}
          style={{ 
            textDecoration: 'none',
            backgroundColor: 'var(--Button-Primary-Enabled-BG)',
            color: 'var(--Button-Primary-Text)',
            padding: 'var(--spacing-xs) var(--spacing-m)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-button)',
            fontWeight: 'var(--font-weight-button)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--spacing-xs)'
          }}
        >
          <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Invoice
        </Link>
      </div>

      {/* Financial Summary Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 'var(--spacing-m)',
        marginBottom: 'var(--spacing-l)'
      }}>
        <div className={styles.sectionCard} style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: 'var(--font-size-h5)', 
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--Text-Primary)',
            marginBottom: 'var(--spacing-xxs)'
          }}>
            {formatCurrency(totalInvoiced)}
          </div>
          <div style={{ 
            fontSize: 'var(--font-size-body4)', 
            color: 'var(--Text-Secondary)' 
          }}>
            Total Invoiced
          </div>
        </div>

        <div className={styles.sectionCard} style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: 'var(--font-size-h5)', 
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--Red-600)',
            marginBottom: 'var(--spacing-xxs)'
          }}>
            {formatCurrency(totalCommission)}
          </div>
          <div style={{ 
            fontSize: 'var(--font-size-body4)', 
            color: 'var(--Text-Secondary)' 
          }}>
            Platform Commission
          </div>
        </div>

        <div className={styles.sectionCard} style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: 'var(--font-size-h5)', 
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--Green-600)',
            marginBottom: 'var(--spacing-xxs)'
          }}>
            {formatCurrency(yourPayout)}
          </div>
          <div style={{ 
            fontSize: 'var(--font-size-body4)', 
            color: 'var(--Text-Secondary)' 
          }}>
            Your Payout
          </div>
        </div>
      </div>

      {/* Invoices List */}
      <div>
        <h3 style={{ 
          fontSize: 'var(--font-size-subtitle3)', 
          fontWeight: 'var(--font-weight-subtitle3)',
          color: 'var(--Text-Primary)',
          marginBottom: 'var(--spacing-s)'
        }}>
          Project Invoices
        </h3>

        {invoices && invoices.length > 0 ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 'var(--spacing-s)' 
          }}>
            {invoices.map((invoice) => (
              <Link
                key={invoice.id}
                to={`/invoices/${invoice.id}`}
                className={styles.sectionCard}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  textDecoration: 'none',
                  transition: 'var(--transition-all)',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                }}
              >
                <div>
                  <div style={{ 
                    fontSize: 'var(--font-size-body3)', 
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--Text-Primary)',
                    marginBottom: 'var(--spacing-xxs)'
                  }}>
                    Invoice #{invoice.stripe?.invoiceNumber || invoice.uniqueRefNumber || invoice.id.slice(-6)}
                  </div>
                  <div style={{ 
                    fontSize: 'var(--font-size-body5)', 
                    color: 'var(--Text-Secondary)' 
                  }}>
                    {formatDate(invoice.createdAt)}
                  </div>
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 'var(--spacing-m)' 
                }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ 
                      fontSize: 'var(--font-size-body3)', 
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--Text-Primary)',
                      marginBottom: 'var(--spacing-xxs)'
                    }}>
                      {formatCurrency(invoice.financials?.subtotal * 100 || 0)}
                    </div>
                    <span style={{
                      fontSize: 'var(--font-size-body5)',
                      padding: 'var(--spacing-xxs) var(--spacing-xs)',
                      borderRadius: 'var(--radius-sm)',
                      backgroundColor: invoice.status === 'paid' ? 'var(--Green-100)' : 
                                      invoice.status === 'open' ? 'var(--Blue-100)' :
                                      'var(--Grey-100)',
                      color: invoice.status === 'paid' ? 'var(--Green-800)' : 
                             invoice.status === 'open' ? 'var(--Blue-800)' :
                             'var(--Grey-800)',
                      fontWeight: 'var(--font-weight-medium)',
                      textTransform: 'capitalize'
                    }}>
                      {invoice.status || 'draft'}
                    </span>
                  </div>
                  <svg 
                    style={{ width: '1.25rem', height: '1.25rem', color: 'var(--Grey-400)' }} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className={styles.sectionCard} style={{ 
            textAlign: 'center', 
            padding: 'var(--spacing-xl)'
          }}>
            <svg 
              style={{ 
                width: '3rem', 
                height: '3rem', 
                color: 'var(--Grey-300)',
                margin: '0 auto var(--spacing-m)'
              }} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div style={{ 
              fontSize: 'var(--font-size-body3)', 
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--Text-Secondary)',
              marginBottom: 'var(--spacing-xs)'
            }}>
              No invoices created yet
            </div>
            <p style={{ 
              fontSize: 'var(--font-size-body4)', 
              color: 'var(--Text-Tertiary)',
              marginBottom: 'var(--spacing-m)'
            }}>
              Create your first invoice to start tracking payments for this project
            </p>
            <Link 
              to={`/invoices/new?project=${project.id}`}
              style={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-xs)',
                padding: 'var(--spacing-xs) var(--spacing-m)',
                backgroundColor: 'var(--Button-Primary-Enabled-BG)',
                color: 'var(--Button-Primary-Text)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-button)',
                fontWeight: 'var(--font-weight-button)',
                textDecoration: 'none',
                transition: 'var(--transition-all)'
              }}
            >
              <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create First Invoice
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicesTab;